/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    ControlType,
    Heading,
    ISettingGroupDefinition,
    Levels,
    SettingGroupDefinition,
    StringResource
} from "@nuance/hierarchical-settings-ui-domain";
import {
    IFieldDefinitionPanelHeadingData,
    IFieldDefinitionPanelHeadingProps,
    IFieldDefinitionsPanelData,
    IFieldDefinitionsPanelProps
} from "../Panels";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { IDomainDataMapper } from "./IDomainDataMapper";

const logger: ILogger = getLogger();

/**
 * Maps heading data from the setting group definition
 * to/from a heading panel data object.
 */
export class DomainDataMapperHeading implements IDomainDataMapper {
    /**
     * Control type expected by this mapper
     */
    readonly RequiredControlType = ControlType.Heading;

    /**
     * Setting group definition object
     */
    readonly definition: ISettingGroupDefinition;

    constructor(definition: ISettingGroupDefinition) {
        this.definition = definition;
    }

    /**
     * Builds a heading panel data for adding a new control.
     * @returns Heading panel data
     */
    BuildPanelPropsForNewItem = (): IFieldDefinitionsPanelProps => {
        const result: IFieldDefinitionPanelHeadingProps = {
            common: {
                panelTitle: "Add Heading",
                originalSettingName: "",
                isNew: true,
                isOpen: true
            },
            data: {
                definitionType: ControlType.Heading,
                headingId: "",
                heading: "",
                accessLevels: Levels.Classic,
                visibleRule: ""
            }
        };

        return result;
    };

    /**
     * Builds a heading panel data for a current heading control.
     * @param ctrlGuid Control GUID for the heading
     * @returns Heading panel data
     */
    BuildPanelPropsForExistingItem = (ctrlGuid: string): IFieldDefinitionsPanelProps => {
        const sg = new SettingGroupDefinition(this.definition);
        const h = this.findControl(sg, ctrlGuid);
        const stringResources = sg.UI.StringResources;
        const sr = new StringResource(h.TitleStringId);

        const result: IFieldDefinitionPanelHeadingProps = {
            common: {
                panelTitle: "Edit Heading",
                originalSettingName: h.HeadingId,
                isNew: false,
                isOpen: true
            },
            data: {
                ctrlGuid,
                definitionType: ControlType.Heading,
                headingId: h.HeadingId,
                heading: stringResources.get(sr.MapKey)?.Text ?? "",
                accessLevels: h.AccessLevel,
                visibleRule: h.VisibleRuleName
            }
        };

        return result;
    };

    /**
     * Saves the heading panel data to a new setting group definition.
     * @param data Heading panel data
     * @returns Setting group definition object
     */
    SaveDataToDefinition = (data: IFieldDefinitionsPanelData): ISettingGroupDefinition => {
        if (data.definitionType !== ControlType.Heading) {
            logger.logError("Invalid control type", { type: ControlType[data.definitionType] });
            throw new Error("Invalid definition type.");
        }

        const hd = data as IFieldDefinitionPanelHeadingData;
        const sg = new SettingGroupDefinition(this.definition);

        if (data.ctrlGuid === undefined) {
            this.addNewHeading(hd, sg);
        } else {
            this.updateExistingHeading(data.ctrlGuid, hd, sg);
        }

        return sg.toISettingGroupDefinition();
    };

    /**
     * Adds a new heading to the setting definition
     *
     * @param hd Panel heading data
     * @param sg Setting group definition
     */
    private addNewHeading(hd: IFieldDefinitionPanelHeadingData, sg: SettingGroupDefinition): void {
        const h = sg.UI.AddHeading(hd.headingId);
        h.AccessLevel = hd.accessLevels;
        h.VisibleRuleName = hd.visibleRule;

        const sr = new StringResource(h.TitleStringId);
        sr.Text = hd.heading;
        sg.UI.StringResources.set(sr.MapKey, sr);
    }

    /**
     * Updates an existing heading in the setting definition
     *
     * @param hd Panel heading data
     * @param sg Setting group definition
     */
    private updateExistingHeading(
        ctrlGuid: string,
        hd: IFieldDefinitionPanelHeadingData,
        sg: SettingGroupDefinition
    ): void {
        const h = this.findControl(sg, ctrlGuid);

        // user not allowed to change heading id
        if (h.HeadingId !== hd.headingId) {
            logger.logError("Heading id changed", { oldId: h.HeadingId, newId: hd.headingId });
            throw new Error("Heading id can not change.");
        }

        h.AccessLevel = hd.accessLevels;
        h.VisibleRuleName = hd.visibleRule;

        const sr = new StringResource(h.TitleStringId);
        sr.Text = hd.heading;
        sg.UI.StringResources.set(sr.MapKey, sr);
    }

    /**
     * Find the control and field definition for a
     * control GUID
     *
     * @param sg Setting group definition
     * @param ctrlGuid Control GUID
     * @returns TextFormGroup and StringFieldDefinition
     * associated with the control
     */
    private findControl = (sg: SettingGroupDefinition, ctrlGuid: string): Heading => {
        const ctrlIndex = sg.UI.Controls.findIndex(i => i.CtrlGuid == ctrlGuid);
        if (ctrlIndex === -1) {
            logger.logError("Unable to find control", { ctrlGuid });
            throw new Error("Unable to find control.");
        }

        const uiCtrl = sg.UI.Controls[ctrlIndex];
        if (uiCtrl.ControlDiscriminator !== this.RequiredControlType) {
            logger.logError("Control type is not text", {
                type: ControlType[uiCtrl.ControlDiscriminator]
            });
            throw new Error("Invalid control type.  Expecting TextField control.");
        }

        return uiCtrl as Heading;
    };
}
